import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
} from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { logError } from "@/shared/services/ErrorReporting";
import { toast } from "@/utils/toast";
import { db } from "@/integrations/firebase/firestore";

export enum JobStatus {
  IN_PROGRESS = "In Progress",
  COMPLETED = "Completed",
  ERROR = "Error",
  PENDING_UPLOAD = "Uploading",
  QUEUED = "Queued",
  DELETED = "Deleted",
}

export const SUMMARIZATION_MAX_RETRIES = 2;

export const getDisplayStatus = (
  status: JobStatus,
  errorCount: number = 0,
): JobStatus => {
  if (status === JobStatus.ERROR && errorCount <= SUMMARIZATION_MAX_RETRIES) {
    return JobStatus.IN_PROGRESS;
  }
  return status;
};

export interface IJob {
  jobId: string;
  userId: string;
  manuscriptUri: string;
  fileName: string;
  summaryUri?: string;
  uploadedAt: Date;
  status: JobStatus;
  summaryId: string;
  desiredWordCount: number;
  outputLanguage: string;
  manuscriptTitle?: string | null;
  manuscriptAuthor?: string | null;
  markedForDeletion: boolean;
  tenantId?: string;
  displayStatus: JobStatus;
}

export class Job implements IJob {
  public jobId: string;

  public userId: string;

  public manuscriptUri: string;

  public status: JobStatus;

  public uploadedAt: Date;

  public summaryUriDoc: string | undefined;

  public summaryId: string;

  public desiredWordCount: number;

  public outputLanguage: string;

  public manuscriptTitle?: string | null;

  public manuscriptAuthor?: string | null;

  public markedForDeletion: boolean;

  public tenantId?: string;

  public displayStatus: JobStatus;

  get fileName(): string {
    return this.manuscriptUri?.split("/")?.pop() || "";
  }

  get summaryUri(): string {
    return (
      this.summaryUriDoc ||
      this.manuscriptUri
        .split("/")
        .slice(0, -1)
        .concat(["output", "summary.docx"])
        .join("/")
    );
  }

  constructor(
    jobId: string,
    userId: string,
    manuscriptUri: string,
    status: JobStatus,
    uploadedAt: Date,
    summaryUriDoc: string | undefined,
    summaryId: string,
    desiredWordCount: number,
    outputLanguage: string,
    markedForDeletion: boolean = true,
    tenantId?: string,
    errorCount: number = 0,
  ) {
    this.jobId = jobId;
    this.userId = userId;
    this.manuscriptUri = manuscriptUri;
    this.status = status;
    this.uploadedAt = uploadedAt;
    this.summaryUriDoc = summaryUriDoc;
    this.summaryId = summaryId;
    this.desiredWordCount = desiredWordCount;
    this.outputLanguage = outputLanguage;
    this.markedForDeletion = markedForDeletion;
    this.tenantId = tenantId;
    this.displayStatus = getDisplayStatus(status, errorCount);
  }
}

export const useJobs = (limitCount: number) => {
  const { authUser } = useAuth();
  const [jobs, setJobs] = useState<IJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (error) {
      logError(error);
      toast.error(error.message);
      setError(null);
    }
  }, [error]);

  useEffect(() => {
    if (!authUser) {
      setIsLoading(false);
      return;
    }

    const q = query(
      collection(db, "jobs"),
      where("user_id", "==", authUser.uid),
      orderBy("uploaded_at", "desc"),
      limit(limitCount),
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const jobsData: IJob[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          jobsData.push(
            new Job(
              doc.id,
              data.user_id,
              data.manuscript_uri,
              JobStatus[data.status as keyof typeof JobStatus],
              data.uploaded_at?.toDate() || new Date(),
              data.summary_uri || undefined,
              data.summary_id,
              data.desired_word_count || 2500,
              data.output_language || "en",
              data.marked_for_deletion ?? true,
              data.tenant_id,
              data.errors?.length ?? 0,
            ),
          );
        });
        setJobs(jobsData);
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [authUser, limitCount]);

  return { jobs, isLoading, error };
};
